import React, { Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import EmployeesListPage from "@/pages/EmployeesListPage";
import AppLayout from "@/components/AppLayout";
import { Toaster } from "sonner";

// Rotas pesadas em lazy — reduz bundle inicial e acelera 1º carregamento.
const EmployeeDetailPage = lazy(() => import("@/pages/EmployeeDetailPage"));
const ReportsPage = lazy(() => import("@/pages/ReportsPage"));
const SettingsPage = lazy(() => import("@/pages/SettingsPage"));
const DocumentsPage = lazy(() => import("@/pages/DocumentsPage"));
const EmailHistoryPage = lazy(() => import("@/pages/EmailHistoryPage"));
const AtestadosPage = lazy(() => import("@/pages/AtestadosPage"));
const EpiPage = lazy(() => import("@/pages/EpiPage"));
const PublicSignPage = lazy(() => import("@/pages/PublicSignPage"));
const PublicSignDocumentPage = lazy(() => import("@/pages/PublicSignDocumentPage"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage"));
const BranchesPage = lazy(() => import("@/pages/BranchesPage"));
const UsersPage = lazy(() => import("@/pages/UsersPage"));
const TemplatesPage = lazy(() => import("@/pages/TemplatesPage"));
const TalentsPage = lazy(() => import("@/pages/TalentsPage"));
const CompanyDocsPage = lazy(() => import("@/pages/CompanyDocsPage"));
const SchedulePanelPage = lazy(() => import("@/pages/SchedulePanelPage"));
const ChecklistPage = lazy(() => import("@/pages/ChecklistPage"));
const PublicVistoriaPage = lazy(() => import("@/pages/PublicVistoriaPage"));

const LazyFallback = () => (
  <div className="p-10 text-sm text-slate-500 text-center">Carregando página...</div>
);

// Persistent layout: AppLayout stays mounted across child route changes.
// This prevents the sidebar/nav from unmounting and avoids race conditions
// where a brief data-loading state would blank out the whole shell.
function ProtectedLayout() {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-brand-700">
        Carregando...
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" replace />;
  return (
    <AppLayout>
      <Suspense fallback={<LazyFallback />}>
        <Outlet />
      </Suspense>
    </AppLayout>
  );
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-brand-700">
        Carregando...
      </div>
    );
  }
  if (user) return <Navigate to="/" replace />;
  return children;
}

// Somente o e-mail oficial acessa QUALQUER rota administrativa.
const FINANCE_EMAIL = "segurancadotrabalhocolenghi@gmail.com";

function AdminOnly({ children }) {
  const { user, logout } = useAuth();
  const ok = (user?.email || "").trim().toLowerCase() === FINANCE_EMAIL;
  React.useEffect(() => {
    if (user && !ok) logout();
  }, [user, ok, logout]);
  if (!user) return null;
  if (!ok) return <Navigate to="/login" replace />;
  return children;
}

function FinanceOnly({ children }) {
  const { user, logout } = useAuth();
  const isFinance = (user?.email || "").trim().toLowerCase() === FINANCE_EMAIL;
  React.useEffect(() => {
    if (user && !isFinance) logout();
  }, [user, isFinance, logout]);
  if (!user) return null;
  if (!isFinance) return <Navigate to="/login" replace />;
  return children;
}

function DriverRedirect({ children }) {
  const { user } = useAuth();
  if (user && user.role === "driver") return <Navigate to="/checklist" replace />;
  return children;
}

function DriverOnly({ children }) {
  const { user } = useAuth();
  if (!user) return null;
  return children;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Suspense fallback={<LazyFallback />}>
            <Routes>
              <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
              <Route path="/vistoria" element={<PublicVistoriaPage />} />
              <Route path="/assinar/:token" element={<PublicSignPage />} />
              <Route path="/signature/:id" element={<PublicSignPage />} />
              <Route path="/assinar-documento/:id" element={<PublicSignDocumentPage />} />
              <Route path="/redefinir-senha/:token" element={<ResetPasswordPage />} />
              <Route element={<ProtectedLayout />}>
                <Route path="/" element={<DriverRedirect><DashboardPage /></DriverRedirect>} />
                <Route path="/checklist" element={<DriverOnly><ChecklistPage /></DriverOnly>} />
                <Route path="/colaboradores" element={<DriverRedirect><EmployeesListPage /></DriverRedirect>} />
                <Route path="/colaboradores/:id" element={<DriverRedirect><EmployeeDetailPage /></DriverRedirect>} />
                <Route path="/documentos" element={<DriverRedirect><DocumentsPage /></DriverRedirect>} />
                <Route path="/atestados" element={<DriverRedirect><AtestadosPage /></DriverRedirect>} />
                <Route path="/epi" element={<DriverRedirect><EpiPage /></DriverRedirect>} />
                <Route path="/relatorios" element={<FinanceOnly><ReportsPage /></FinanceOnly>} />
                <Route path="/historico-emails" element={<DriverRedirect><EmailHistoryPage /></DriverRedirect>} />
                <Route path="/configuracoes" element={<FinanceOnly><SettingsPage /></FinanceOnly>} />
                <Route path="/filiais" element={<AdminOnly><BranchesPage /></AdminOnly>} />
                <Route path="/usuarios" element={<AdminOnly><UsersPage /></AdminOnly>} />
                <Route path="/modelos" element={<AdminOnly><TemplatesPage /></AdminOnly>} />
                <Route path="/banco-talentos" element={<AdminOnly><TalentsPage /></AdminOnly>} />
                <Route path="/documentos-empresa" element={<AdminOnly><CompanyDocsPage /></AdminOnly>} />
                <Route path="/escalas" element={<AdminOnly><SchedulePanelPage /></AdminOnly>} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
